// PDKS kiosk — single-flow tablet app (Track C / Batch C2+C3).
//
// Flow (kiosk-machine.ts owns the transitions; this component owns the
// side effects): idle QR screen → PIN fallback → in/out choice →
// transport punch → success (brand surface) / failure → auto-return.
//
// The punch goes through the ONE transport seam (src/api/punch.ts):
// HTTP /v1/kiosk/punch in production, with an explicit mock injection in tests.
// App only sees the PunchTransport interface. Late
// transport resolutions are inert by construction: the reducer ignores
// PUNCH_OK / PUNCH_FAIL outside the submitting step.

import { useEffect, useReducer, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { Classification } from "@novemai/error-envelope";

import {
  KioskPunchError,
  punchTransport,
  type PunchDirection,
  type KioskPunchRequest,
  type PunchTransport,
  type KioskPresenceCodeFetcher,
} from "@/api/punch";
import type { KioskBootstrapState } from "@/bootstrap";
import { KioskShell } from "@/components/KioskShell";
import { KIOSK_DEFAULT_LOCALE } from "@/i18n";
import {
  AUTO_RETURN_MS,
  canSubmitPin,
  KIOSK_INACTIVITY_MS,
  KIOSK_INITIAL_STATE,
  kioskReducer,
} from "@/kiosk-machine";
import { DirectionScreen } from "@/screens/DirectionScreen";
import { FailureScreen } from "@/screens/FailureScreen";
import { IdleScreen } from "@/screens/IdleScreen";
import { PinScreen } from "@/screens/PinScreen";
import { SuccessScreen } from "@/screens/SuccessScreen";

export interface AppProps {
  /** QR transport seam; production uses the provisioned device credential. */
  readonly fetchPresenceCode?: KioskPresenceCodeFetcher;
  /** Test seam — defaults to the module-level active transport. */
  readonly transport?: PunchTransport;
  /** Test seam — success/failure auto-return delay. */
  readonly autoReturnMs?: number;
  /** Live device config; null/undefined keeps the localized shell fallback. */
  readonly locationLabel?: string | null;
  /**
   * Initial production bootstrap gate. An unavailable seed can recover in
   * place through retryBootstrap; terminal states remain fail-closed.
   */
  readonly bootstrapState?: KioskBootstrapState;
  /**
   * Main-owned initial config resolution. When provided without a settled
   * bootstrapState the app renders an announced loading gate (punching stays
   * blocked) until the resolver settles — the boot window is never blank.
   */
  readonly resolveBootstrap?: () => Promise<KioskBootstrapState>;
  /** Main-owned re-bootstrap seam; only callable from an unavailable gate. */
  readonly retryBootstrap?: () => Promise<KioskBootstrapState>;
}

/** Pre-config pending gate; only App creates it, resolvers never return it. */
interface KioskLoadingGateState {
  readonly status: "loading";
}

type KioskGateState = KioskBootstrapState | KioskLoadingGateState;

interface PunchFailureInfo {
  readonly messageKey: string;
  readonly classification: Classification | undefined;
  readonly retryable: boolean | undefined;
  readonly retryAfterMs: number | undefined;
  readonly traceId: string | undefined;
  readonly ambiguous: boolean;
}

function punchFailureInfoFrom(error: unknown): PunchFailureInfo {
  return error instanceof KioskPunchError
    ? {
        messageKey: error.messageKey,
        classification: error.classification,
        retryable: error.retryable,
        retryAfterMs: error.retryAfterMs,
        traceId: error.traceId,
        ambiguous: error.ambiguous,
      }
    : {
        messageKey: "errors.code.platform.unhandled_error",
        classification: undefined,
        retryable: undefined,
        retryAfterMs: undefined,
        traceId: undefined,
        ambiguous: false,
      };
}

export function App({
  transport = punchTransport,
  autoReturnMs = AUTO_RETURN_MS,
  locationLabel = null,
  bootstrapState,
  resolveBootstrap,
  retryBootstrap,
  fetchPresenceCode,
}: AppProps = {}): React.ReactElement {
  const [state, dispatch] = useReducer(kioskReducer, KIOSK_INITIAL_STATE);
  const [activityVersion, recordActivity] = useReducer((version: number) => version + 1, 0);
  // Main mounts this root only once. Keep the recoverable bootstrap state here
  // so a temporary config outage never needs a reload or a second React root.
  const [resolvedBootstrapState, setResolvedBootstrapState] = useState<
    KioskGateState | undefined
  >(() =>
    bootstrapState ??
    (resolveBootstrap !== undefined ? { status: "loading" } : undefined),
  );
  const [isBootstrapRetrying, setBootstrapRetrying] = useState(false);
  const retryInFlightRef = useRef(false);
  const retryGenerationRef = useRef(0);
  const isMountedRef = useRef(true);
  const { t } = useTranslation();

  // A late config response must not update an unmounted kiosk. The generation
  // also makes the current in-flight retry the only response allowed to win.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      retryGenerationRef.current += 1;
      retryInFlightRef.current = false;
    };
  }, []);

  // Boot resolution runs inside React so the worker sees an announced loading
  // gate instead of an empty #root (SEM-4922). Fail-closed on any resolver
  // throw: land on the unavailable gate, never reveal the punch flow.
  useEffect(() => {
    if (resolveBootstrap === undefined || bootstrapState !== undefined) return;
    let cancelled = false;
    void Promise.resolve()
      .then(resolveBootstrap)
      .then(
        (nextState) => {
          if (!cancelled) setResolvedBootstrapState(nextState);
        },
        () => {
          if (!cancelled) {
            setResolvedBootstrapState({
              status: "unavailable",
              locale: KIOSK_DEFAULT_LOCALE,
              locationLabel: null,
            });
          }
        },
      );
    return () => {
      cancelled = true;
    };
  }, [resolveBootstrap, bootstrapState]);

  // Terminal screens drift back to idle on their own — a kiosk must never
  // sit on a personal result when the next person walks up.
  useEffect(() => {
    if (state.step !== "success" && state.step !== "failure") return;
    const timer = window.setTimeout(() => {
      if (state.step === "failure") transport.cancelPendingIntent?.();
      dispatch({ type: "RESET" });
    }, autoReturnMs);
    return () => window.clearTimeout(timer);
  }, [state.step, autoReturnMs, transport]);

  // A shared kiosk may never leave a partial PIN or a direction choice in
  // view for the next worker. Do not arm this while a request is in-flight:
  // the reducer's late-result guard must remain the only terminal safety path.
  useEffect(() => {
    if (state.step !== "pin" && state.step !== "direction") return;
    const timer = window.setTimeout(() => {
      transport.cancelPendingIntent?.();
      dispatch({ type: "RESET" });
    }, KIOSK_INACTIVITY_MS);
    return () => window.clearTimeout(timer);
  }, [state.step, activityVersion, transport]);

  const submitPunch = (intent: KioskPunchRequest, event: "PUNCH" | "RETRY"): void => {
    if (event === "PUNCH") {
      dispatch({ type: "PUNCH", direction: intent.direction });
    } else {
      dispatch({ type: "RETRY" });
    }
    transport.punch(intent).then(
      (result) => dispatch({ type: "PUNCH_OK", result }),
      (error: unknown) => {
        const failure = punchFailureInfoFrom(error);
        dispatch({
          type: "PUNCH_FAIL",
          ...failure,
          ...(failure.ambiguous && { retryIntent: intent }),
        });
      },
    );
  };

  const handlePunch = (direction: PunchDirection): void => {
    if (state.step !== "direction") return;
    const intent = Object.freeze({
      pin: state.pin,
      direction,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    submitPunch(intent, "PUNCH");
  };

  const handleRetry = (): void => {
    if (state.step !== "failure") return;
    if (state.retryIntent !== undefined) {
      submitPunch(state.retryIntent, "RETRY");
      return;
    }
    // A definitive response has no durable intent to replay. Always return to
    // a blank PIN screen and ensure the next attempt starts a fresh request.
    transport.cancelPendingIntent?.();
    dispatch({ type: "RETRY" });
  };

  const retryBootstrapConfig = (): void => {
    if (
      resolvedBootstrapState?.status !== "unavailable" ||
      retryBootstrap === undefined ||
      retryInFlightRef.current
    ) {
      return;
    }

    retryInFlightRef.current = true;
    const retryGeneration = ++retryGenerationRef.current;
    setBootstrapRetrying(true);

    // A synchronous runtime/test throw follows the same fail-closed path as a
    // rejected config fetch, while the actual fetch starts in this click.
    let retryPromise: Promise<KioskBootstrapState>;
    try {
      retryPromise = Promise.resolve(retryBootstrap());
    } catch (error) {
      retryPromise = Promise.reject(error);
    }

    void retryPromise
      .then((nextBootstrapState) => {
        if (
          !isMountedRef.current ||
          retryGenerationRef.current !== retryGeneration
        ) {
          return;
        }
        setResolvedBootstrapState(nextBootstrapState);
      })
      .catch(() => {
        // The current state is already unavailable. Intentionally retain it:
        // a retry error must never reveal a usable punch flow.
      })
      .finally(() => {
        if (
          !isMountedRef.current ||
          retryGenerationRef.current !== retryGeneration
        ) {
          return;
        }
        retryInFlightRef.current = false;
        setBootstrapRetrying(false);
      });
  };

  const autoReturnSeconds = Math.round(autoReturnMs / 1000);
  const bootstrapStatus = resolvedBootstrapState?.status ?? "ready";
  if (bootstrapStatus !== "ready") {
    const guidance =
      bootstrapStatus === "loading"
        ? t("kiosk.bootstrap.loading")
        : bootstrapStatus === "unprovisioned"
          ? t("kiosk.bootstrap.unprovisioned")
          : bootstrapStatus === "disabled"
            ? t("kiosk.bootstrap.disabled")
            : t("kiosk.bootstrap.unavailable");
    // The header slot is the LOCATION line, and each blocked state has a
    // different reason for not having one. A single "setup unavailable" label
    // read as a fault on a terminal that was merely never registered, and
    // duplicated the guidance below it.
    const blockedLocationLabel =
      bootstrapStatus === "loading"
        ? t("kiosk.location_loading")
        : bootstrapStatus === "unprovisioned"
          ? t("kiosk.location_unprovisioned")
          : bootstrapStatus === "disabled"
            ? t("kiosk.location_disabled")
            : t("kiosk.location_unavailable");
    return (
      <KioskShell locationLabel={blockedLocationLabel}>
        <div className="flex flex-1 items-center justify-center px-16 pb-10">
          <div className="flex max-w-[680px] flex-col items-center text-center">
            <p
              role="status"
              aria-live="polite"
              className="m-0 font-serif text-[clamp(30px,4vw,48px)] leading-tight text-[var(--fg-primary)]"
            >
              {guidance}
            </p>
            {bootstrapStatus === "unavailable" && (
              <button
                type="button"
                onClick={retryBootstrapConfig}
                disabled={isBootstrapRetrying || retryBootstrap === undefined}
                aria-busy={isBootstrapRetrying}
                className="mt-10 cursor-pointer rounded-full border-0 bg-[var(--accent)] px-11 py-4 font-sans text-[17px] font-semibold text-[var(--fg-on-accent)] shadow-[var(--shadow-md)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring)] active:bg-[var(--accent-active)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isBootstrapRetrying
                  ? t("kiosk.bootstrap.retrying")
                  : t("kiosk.bootstrap.retry")}
              </button>
            )}
          </div>
        </div>
      </KioskShell>
    );
  }

  const resolvedLocationLabel =
    resolvedBootstrapState?.status === "ready"
      ? resolvedBootstrapState.locationLabel
      : locationLabel;

  return (
    <KioskShell
      tone={state.step === "success" ? "brand" : "surface"}
      locationLabel={resolvedLocationLabel}
    >
      {state.step === "idle" && (
        <IdleScreen demoMode={transport.kind === "mock"} fetchPresenceCode={fetchPresenceCode} onStart={() => dispatch({ type: "START" })} />
      )}

      {state.step === "pin" && (
        <PinScreen
          pin={state.pin}
          canSubmit={canSubmitPin(state)}
          onDigit={(digit) => {
            recordActivity();
            dispatch({ type: "DIGIT", digit });
          }}
          onBackspace={() => {
            recordActivity();
            dispatch({ type: "BACKSPACE" });
          }}
          onSubmit={() => {
            recordActivity();
            dispatch({ type: "PIN_SUBMIT" });
          }}
          onBack={() => {
            transport.cancelPendingIntent?.();
            dispatch({ type: "BACK" });
          }}
        />
      )}

      {(state.step === "direction" || state.step === "submitting") && (
        <DirectionScreen
          submittingDirection={state.step === "submitting" ? state.direction : null}
          onPunch={handlePunch}
          onBack={() => {
            transport.cancelPendingIntent?.();
            dispatch({ type: "BACK" });
          }}
        />
      )}

      {state.step === "success" && (
        <SuccessScreen
          result={state.result}
          autoReturnSeconds={autoReturnSeconds}
          onDone={() => dispatch({ type: "RESET" })}
        />
      )}

      {state.step === "failure" && (
        <FailureScreen
          messageKey={state.messageKey}
          classification={state.classification}
          retryable={state.retryable}
          retryAfterMs={state.retryAfterMs}
          traceId={state.traceId}
          autoReturnSeconds={autoReturnSeconds}
          onRetry={handleRetry}
          onHome={() => {
            transport.cancelPendingIntent?.();
            dispatch({ type: "RESET" });
          }}
        />
      )}
    </KioskShell>
  );
}
